// One organization (D56 screen 2 and 3): plan, trial days left, members, apps and keys, and the
// actions: extend the trial, change the plan, suspend or restore ingest, switch a past-due
// subscription to reais on the switch its owner prepared (D88 addendum), and per app the kill
// switch and a capture-rate override with an expiry, published to the edge policy (D69).
// Opening it is a view the customer sees in their audit log.
// Console D's detail page (APS-412, admin-d.md §3B): the head with "Ver como o cliente" and the
// "Ações" menu, whose items open their form window under the head; a strip of figures; members
// in the main column, the organization's facts and the staff flags beside them; the apps full
// width; then the plan fit, staff's actions here, and the Danger zone last.
import { PageHead, Section, Status, Strip, usePopover } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Fragment, type ReactNode, useId, useState } from 'react'
import {
  type AppDetail,
  auditQuery,
  changePlan,
  clearCapture,
  type Erasure,
  extendTrial,
  makeOwner,
  type OrgDetail,
  orgErasureQuery,
  orgQuery,
  type Plan,
  setCapture,
  setKillSwitch,
  setKind,
  setSuspended,
  switchToReaisNow,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import {
  DataTable,
  formatUtc,
  IdText,
  parseUtc,
  RowMenu,
  StatusWord,
  UtcField,
} from '../components/kit'
import { useCrumbs } from '../components/Shell'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { percent } from '../lib/format'
import { AuditTable } from './Audit'
import { CancelRows, OrgBilling } from './OrgBilling'
import { OrgErasurePanel } from './OrgErasure'
import { OrgPostHog } from './OrgPostHog'
import { OrgSla } from './OrgSla'
import { PlanFitFlags, PlanFitPanel } from './PlanFit'
import { PLAN_LABEL } from './pills'

const PLANS: Plan[] = ['free', 'trial', 'pro', 'enterprise']
type OrgKind = OrgDetail['kind']
const KINDS: OrgKind[] = ['customer', 'internal', 'sandbox']
const KIND_LABEL: Record<OrgKind, MessageId> = {
  customer: 'kind.customer',
  sandbox: 'kind.sandbox',
  internal: 'kind.internal',
}
const ROLE_LABEL: Record<OrgDetail['members'][number]['role'], MessageId> = {
  owner: 'role.owner',
  admin: 'role.admin',
  member: 'role.member',
}
/** An email or a bundle id that breaks after its `@` and dots before anywhere else (APS-500). */
function breakable(text: string): ReactNode[] {
  let at = 0
  return text.split(/(?<=[@.])/).map((part) => {
    const start = at
    at += part.length
    return start === 0 ? (
      part
    ) : (
      <Fragment key={start}>
        <wbr />
        {part}
      </Fragment>
    )
  })
}

/** An app key (D5, `a-` and 20 characters) as `a-k3x9…7f2q`: its head shows it is a key. */
const KEY_HEAD = 6
const PLATFORM: Record<AppDetail['platform'], string> = {
  ios: 'iOS',
  android: 'Android',
  react_native: 'React Native',
}

/** `hours` from now as the UtcField's text (`AAAA-MM-DD HH:MM`, UTC): the expiry's default. */
export function utcIn(hours: number, now = Date.now()): string {
  return formatUtc(new Date(now + hours * 3_600_000).toISOString())
}

/** What the head's "Ações" menu opened, shown under the head. */
type OrgAction = 'trial' | 'plan' | 'kind' | 'suspend' | 'switch'

export function OrganizationPage() {
  const { t, date } = useI18n()
  const { orgId } = useParams({ strict: false }) as { orgId: string }
  const org = useQuery(orgQuery(orgId))
  const audit = useQuery(auditQuery({ orgId, limit: 10 }))
  // The same query as the Danger zone's: an open erasure is said in the head too.
  const erasure = useQuery(orgErasureQuery(orgId)).data?.erasure
  const [action, setAction] = useState<OrgAction | null>(null)
  useCrumbs(org.data ? [{ label: org.data.name }] : [])

  const crumb = <Link to="/orgs">{t('nav.orgs')}</Link>
  if (org.isPending)
    return (
      <main id="main" className="pg">
        <Loading />
      </main>
    )
  if (org.isError)
    return (
      <main id="main" className="pg">
        <PageHead crumb={crumb} title={t('org.title')} />
        <ErrorState error={org.error} retry={() => org.refetch()} />
      </main>
    )
  const o = org.data

  return (
    <main id="main" className="pg">
      <PageHead
        crumb={crumb}
        title={<span translate="no">{o.name}</span>}
        meta={
          <>
            {t(PLAN_LABEL[o.plan])} · {t('org.kindMeta', { kind: o.kind })} ·{' '}
            <StatusWord kind="org" status={o.status} /> ·{' '}
            {erasure && erasure.status !== 'done' && erasure.status !== 'canceled' ? (
              <>
                <ErasureWord erasure={erasure} /> ·{' '}
              </>
            ) : null}
            {t('org.createdOn', { date: date(o.created_at) })}
          </>
        }
        actions={
          <>
            <Link className="btn" to="/orgs/$orgId/view" params={{ orgId }}>
              {t('org.viewAs')}
            </Link>
            <OrgActionsMenu org={o} onPick={setAction} />
          </>
        }
      />
      <OrgActionWindow org={o} action={action} onClose={() => setAction(null)} />

      <OrgStrip org={o} />

      <div className="grid2 org-cols">
        <div className="sections">
          <Members org={o} />
        </div>
        <aside className="sections" aria-label={t('org.side')}>
          <Section title={t('org.side')}>
            <OrgFacts org={o} />
          </Section>
          <PlanFitFlags orgId={orgId} />
          <OrgPostHog orgId={orgId} />
          <OrgSla orgId={orgId} />
        </aside>
      </div>

      <Section title={t('org.apps')}>
        {o.projects.length === 0 ? (
          <Empty title={t('org.noProjects')} />
        ) : (
          o.projects.map((p) => (
            <ProjectApps
              key={p.id}
              project={p}
              orgId={orgId}
              suspended={o.status === 'suspended'}
            />
          ))
        )}
      </Section>

      <OrgBilling org={o} />

      <PlanFitPanel orgId={orgId} />

      <Section title={t('org.audit')}>
        {audit.isPending ? (
          <Loading />
        ) : audit.isError ? (
          <ErrorState error={audit.error} retry={() => audit.refetch()} />
        ) : (
          <AuditTable entries={audit.data} />
        )}
      </Section>

      <OrgErasurePanel orgId={orgId} before={<CancelRows org={o} />} />
    </main>
  )
}

/** An open erasure in the head's meta: `○ exclusão agendada até 27 de out.`, running or failed. */
function ErasureWord({ erasure }: { erasure: Erasure }) {
  const { t, date } = useI18n()
  const [s, glyph] =
    erasure.status === 'scheduled'
      ? (['on', '○'] as const)
      : erasure.status === 'running'
        ? (['on', '◐'] as const)
        : (['fire', '×'] as const)
  return (
    <Status
      s={s}
      glyph={glyph}
      label={t('org.erasureMeta', { status: erasure.status, date: date(erasure.execute_after) })}
    />
  )
}

/** The head's "Ações ▾": Console D's popover (.pop, .pal-i), the destructive item last. */
function OrgActionsMenu({ org, onPick }: { org: OrgDetail; onPick: (a: OrgAction) => void }) {
  const { t } = useI18n()
  const pop = usePopover('', 'below')
  const id = useId()
  const suspended = org.status === 'suspended'
  const safe: [OrgAction, string][] = [
    ...(org.plan === 'trial' || org.plan === 'free'
      ? [
          ['trial', t(org.plan === 'free' ? 'org.giveTrial' : 'org.extendTrial')] as [
            OrgAction,
            string,
          ],
        ]
      : []),
    ['plan', t('org.changePlan')],
    ['kind', t('org.changeKind')],
    ...(suspended ? [['suspend', t('org.restore')] as [OrgAction, string]] : []),
  ]
  const danger: [OrgAction, string][] = [
    ...(org.billing_switch?.state === 'prepared'
      ? [['switch', t('org.switchNow')] as [OrgAction, string]]
      : []),
    ...(suspended ? [] : [['suspend', t('org.suspend')] as [OrgAction, string]]),
  ]
  const pick = (a: OrgAction) => {
    pop.close()
    onPick(a)
  }
  // Its right edge on the button's (the button is the head's last): never past the content.
  const right = pop.anchor.current
    ? document.documentElement.clientWidth - pop.anchor.current.getBoundingClientRect().right
    : undefined
  return (
    <>
      <button
        ref={pop.anchor}
        type="button"
        className="btn"
        aria-expanded={pop.open}
        aria-controls={pop.open ? id : undefined}
        aria-haspopup="true"
        onClick={pop.toggle}
      >
        {t('org.actions')} <span aria-hidden="true">▾</span>
      </button>
      {pop.open ? (
        <section
          ref={pop.pop}
          id={id}
          className="pop row-pop"
          style={{ ...pop.style, left: 'auto', right }}
          aria-label={t('org.actions')}
        >
          {safe.map(([a, label]) => (
            <button key={label} type="button" className="pal-i" onClick={() => pick(a)}>
              {label}
            </button>
          ))}
          {safe.length > 0 && danger.length > 0 ? <hr /> : null}
          {danger.map(([a, label]) => (
            <button key={label} type="button" className="pal-i danger" onClick={() => pick(a)}>
              {label}
            </button>
          ))}
        </section>
      ) : null}
    </>
  )
}

/** The form window the menu opened, under the head: what it needs, then the Motivo. */
function OrgActionWindow({
  org,
  action,
  onClose,
}: {
  org: OrgDetail
  action: OrgAction | null
  onClose: () => void
}) {
  const { t } = useI18n()
  const orgId = org.id
  const refresh = [['org', orgId], ['orgs']]
  const trial = useAdminMutation(extendTrial, refresh)
  const plan = useAdminMutation(changePlan, refresh)
  const kind = useAdminMutation(setKind, refresh)
  const suspend = useAdminMutation(setSuspended, refresh)
  const switchNow = useAdminMutation(switchToReaisNow, refresh)
  const ids = { days: useId(), plan: useId(), kind: useId() }
  const suspended = org.status === 'suspended'
  const shown = { open: true, onClose }
  if (action === 'trial')
    return (
      <ActionForm
        {...shown}
        label={t(org.plan === 'free' ? 'org.giveTrial' : 'org.extendTrial')}
        confirm={t('org.extendTrialConfirm')}
        onSubmit={(reason, form) =>
          trial.mutateAsync({ orgId, days: Number(form.get('days')), reason })
        }
      >
        <div className="field-d">
          <label className="lbl" htmlFor={ids.days}>
            {t('org.days')}
          </label>
          <input
            id={ids.days}
            name="days"
            className="input mono num-field"
            type="number"
            min={1}
            max={60}
            defaultValue={7}
            required
          />
        </div>
      </ActionForm>
    )
  if (action === 'plan')
    return (
      <ActionForm
        {...shown}
        label={t('org.changePlan')}
        confirm={t('org.changePlanConfirm')}
        onSubmit={(reason, form) =>
          plan.mutateAsync({ orgId, plan: form.get('plan') as Plan, reason })
        }
      >
        <div className="field-d">
          <label className="lbl" htmlFor={ids.plan}>
            {t('org.newPlan')}
          </label>
          <select id={ids.plan} name="plan" className="select" defaultValue={org.plan}>
            {PLANS.map((p) => (
              <option key={p} value={p} disabled={p === org.plan}>
                {t(PLAN_LABEL[p])}
              </option>
            ))}
          </select>
        </div>
      </ActionForm>
    )
  if (action === 'kind')
    return (
      <ActionForm
        {...shown}
        label={t('org.changeKind')}
        confirm={t('org.changeKindSubmit')}
        reasonHint={t('org.changeKindConfirm')}
        onSubmit={(reason, form) =>
          kind.mutateAsync({ orgId, kind: form.get('kind') as OrgKind, reason })
        }
      >
        <div className="field-d">
          <label className="lbl" htmlFor={ids.kind}>
            {t('org.newKind')}
          </label>
          <select id={ids.kind} name="kind" className="select" defaultValue={org.kind}>
            {KINDS.map((k) => (
              <option key={k} value={k} disabled={k === org.kind}>
                {t(KIND_LABEL[k])}
              </option>
            ))}
          </select>
        </div>
      </ActionForm>
    )
  if (action === 'suspend')
    return (
      <ActionForm
        {...shown}
        label={suspended ? t('org.restore') : t('org.suspend')}
        confirm={suspended ? t('org.restoreConfirm') : t('org.suspendConfirm')}
        danger={!suspended}
        onSubmit={(reason) => suspend.mutateAsync({ orgId, suspend: !suspended, reason })}
      />
    )
  if (action === 'switch')
    return (
      <ActionForm
        {...shown}
        label={t('org.switchNow')}
        confirm={t('org.switchNowConfirm')}
        danger
        reasonHint={t('org.switchNowHint')}
        onSubmit={(reason) => switchNow.mutateAsync({ orgId, reason })}
      />
    )
  return null
}

/** Four figures: the plan, sessions captured this period, retention, members. */
function OrgStrip({ org: o }: { org: OrgDetail }) {
  const { t, date, number } = useI18n()
  const limit = o.usage ? (o.usage.cap_sessions ?? o.usage.included_sessions) : null
  return (
    <Strip
      label={t('org.strip')}
      items={[
        {
          dt: t('org.fact.plan'),
          dd: t(PLAN_LABEL[o.plan]),
          d:
            o.trial_days_left === null
              ? t('org.noTrial')
              : t('org.trialLeft', {
                  count: o.trial_days_left,
                  date: date(o.trial_ends_at ?? Date.now()),
                }),
        },
        {
          dt: t('org.fact.usage'),
          // The metering job's latest period: unknown until it ran once, then one dash alone.
          dd: o.usage ? number(o.usage.captured_sessions) : '—',
          small: limit ? t('org.ofLimit', { limit: number(limit) }) : undefined,
          // Two lines (Mosaic): the sessions' state, then the second billed unit (D110,
          // APS-351); a cap that stopped either is a warn status word.
          d:
            o.usage || o.detail_capped_at ? (
              <>
                <span className="usage-line">
                  {o.detail_capped_at ? (
                    <Status
                      s="miss"
                      glyph="!"
                      label={t('org.capped', { date: date(o.detail_capped_at) })}
                    />
                  ) : o.usage && limit ? (
                    t('org.used', { share: percent(number, o.usage.captured_sessions / limit, 0) })
                  ) : (
                    t('org.thisPeriod')
                  )}
                </span>
                {o.usage ? (
                  <span className="usage-line">
                    {o.usage.replay_capped_at ? (
                      <Status
                        s="miss"
                        glyph="!"
                        label={t('org.replayCapped', { date: date(o.usage.replay_capped_at) })}
                      />
                    ) : (
                      t('org.replayUsed', {
                        sessions: number(o.usage.replay_sessions),
                        included: number(o.usage.included_replay_sessions),
                      })
                    )}
                  </span>
                ) : null}
              </>
            ) : undefined,
        },
        {
          dt: t('org.fact.retention'),
          dd: number(o.retention_days),
          small: t('org.retentionUnit', { count: o.retention_days }),
        },
        {
          dt: t('org.members'),
          dd: number(o.members.length),
          d: t('org.projectsApps', {
            projects: o.projects.length,
            apps: o.projects.reduce((n, p) => n + p.apps.length, 0),
          }),
        },
      ]}
    />
  )
}

/**
 * The side column's facts: what identifies the organization, and its billing switch, a sentence
 * that sits under its label as a prose row (.stack).
 */
function OrgFacts({ org: o }: { org: OrgDetail }) {
  const { t, date } = useI18n()
  return (
    <dl className="kv">
      <dt>{t('org.fact.id')}</dt>
      <dd>
        <IdText id={o.id} />
      </dd>
      <dt>{t('org.fact.kind')}</dt>
      <dd>{t(KIND_LABEL[o.kind])}</dd>
      <dt>{t('org.fact.created')}</dt>
      <dd>{date(o.created_at)}</dd>
      {o.billing_switch ? (
        <>
          <dt>{t('org.fact.billingSwitch')}</dt>
          <dd className="stack">
            {o.billing_switch.state === 'scheduled' && o.billing_switch.starts_at
              ? t('org.switchScheduled', { date: date(o.billing_switch.starts_at) })
              : t('org.switchPrepared')}
          </dd>
        </>
      ) : null}
    </dl>
  )
}

function Members({ org: o }: { org: OrgDetail }) {
  const { t, date } = useI18n()
  const orgId = o.id
  const owner = useAdminMutation(makeOwner, [['org', orgId], ['orgs']])
  const [promote, setPromote] = useState<string | null>(null)
  return (
    <Section title={t('org.members')} meta={t('orgs.count', { count: o.members.length })}>
      {o.members.length === 0 ? (
        <Empty title={t('org.noMembers')} />
      ) : (
        <DataTable
          label={t('table.of', { name: t('org.members') })}
          minWidth={560}
          className="phone-fit"
        >
          <thead>
            <tr>
              <th scope="col">{t('org.col.name')}</th>
              <th scope="col" className="wide">
                {t('org.col.email')}
              </th>
              <th scope="col" className="wide">
                {t('org.col.role')}
              </th>
              <th scope="col" className="wide">
                {t('org.col.since')}
              </th>
              <th scope="col" className="act">
                <span className="vh">{t('org.col.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {o.members.map((m) => (
              <tr key={m.user_id}>
                <td className="wrap">
                  {m.name ? <span translate="no">{m.name}</span> : t('common.none')}
                  <span className="t2 narrow" translate="no">
                    {breakable(m.email)}
                  </span>
                  <span className="t2 narrow">
                    {t(ROLE_LABEL[m.role])} · {date(m.created_at)}
                  </span>
                </td>
                <td className="wrap wide" translate="no">
                  {breakable(m.email)}
                </td>
                <td className="wide">{t(ROLE_LABEL[m.role])}</td>
                <td className="wide">{date(m.created_at)}</td>
                <td className="act">
                  <MemberMenu member={m} onPromote={() => setPromote(m.user_id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {promote ? (
        <div className="org-win">
          <ActionForm
            open
            onClose={() => setPromote(null)}
            label={t('org.makeOwnerOf', {
              name: o.members.find((m) => m.user_id === promote)?.email ?? '',
            })}
            confirm={t('org.makeOwnerConfirm')}
            reasonHint={t('erasure.reasonHint')}
            noEmails
            onSubmit={(reason) => owner.mutateAsync({ orgId, userId: promote, reason })}
          />
        </div>
      ) : null}
    </Section>
  )
}

/** A member's "⋯": make owner (not for an owner), and erasing the person, which opens its page. */
function MemberMenu({
  member: m,
  onPromote,
}: {
  member: OrgDetail['members'][number]
  onPromote: () => void
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  return (
    <RowMenu
      label={t('org.memberMenu', { name: m.email })}
      items={[
        ...(m.role !== 'owner' ? [{ label: t('org.makeOwner'), onSelect: onPromote }] : []),
        {
          label: t('org.erasePerson'),
          danger: true,
          onSelect: () => navigate({ to: '/users/$userId/erase', params: { userId: m.user_id } }),
        },
      ]}
    />
  )
}

/** One project's apps as a table; each app's controls behind its "⋯". */
function ProjectApps({
  project: p,
  orgId,
  suspended,
}: {
  project: OrgDetail['projects'][number]
  orgId: string
  suspended: boolean
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState<{ app: string; form: AppForm } | null>(null)
  const app = p.apps.find((a) => a.id === open?.app)
  return (
    <div className="org-proj">
      <h3 className="label-small" translate="no">
        {p.name}
      </h3>
      {p.apps.length === 0 ? (
        <p className="dim">{t('org.noApps')}</p>
      ) : (
        <DataTable
          label={t('org.appsOf', { project: p.name })}
          minWidth={640}
          className="phone-fit"
        >
          <thead>
            <tr>
              <th scope="col">{t('app.col.app')}</th>
              <th scope="col">{t('app.col.state')}</th>
              <th scope="col" className="wide">
                {t('app.col.keys')}
              </th>
              <th scope="col" className="wide">
                {t('app.killSwitch')}
              </th>
              <th scope="col" className="wide">
                {t('app.capture')}
              </th>
              <th scope="col" className="act">
                <span className="vh">{t('org.col.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {p.apps.map((a) => (
              <AppRow
                key={a.id}
                app={a}
                suspended={suspended}
                onOpen={(form) => setOpen({ app: a.id, form })}
              />
            ))}
          </tbody>
        </DataTable>
      )}
      {app && open ? (
        <div className="org-win">
          <AppActionWindow app={app} orgId={orgId} form={open.form} onClose={() => setOpen(null)} />
        </div>
      ) : null}
    </div>
  )
}

type AppForm = 'killOn' | 'killOff' | 'capture' | 'clear'

function AppRow({
  app,
  suspended,
  onOpen,
}: {
  app: AppDetail
  suspended: boolean
  onOpen: (form: AppForm) => void
}) {
  const { t, dateTime, number } = useI18n()
  const c = app.control
  const killed = !!c?.kill_switch
  const active = app.keys.filter((k) => !k.revoked_at)
  const keys =
    active.length === 0 ? (
      '—'
    ) : (
      <span className="app-keys">
        {active.map((k) => (
          <IdText key={k.key} id={k.key} head={KEY_HEAD} />
        ))}
      </span>
    )
  const kill = killed
    ? c?.kill_expires_at
      ? t('app.onUntil', { time: dateTime(c.kill_expires_at) })
      : t('app.on')
    : t('app.off')
  const capture =
    c?.capture_rate != null && c.capture_expires_at
      ? t('app.captureUntil', {
          rate: percent(number, c.capture_rate),
          time: dateTime(c.capture_expires_at),
        })
      : t('app.captureNone')
  return (
    <tr>
      <td className="wrap">
        <span translate="no">{app.name}</span>
        <span className="t2" translate="no">
          {PLATFORM[app.platform]} · {breakable(app.bundle_id)}
        </span>
        {/* Under 640 px the keys step under the app, and the kill switch and the capture only
            when they are on (Mosaic): an app at its defaults carries no extra line. */}
        <span className="t2 narrow" translate="no">
          {keys}
        </span>
        {killed ? (
          <span className="t2 narrow app-kill">
            {c?.kill_expires_at
              ? t('app.narrow.killUntil', { time: dateTime(c.kill_expires_at) })
              : t('app.narrow.kill')}
          </span>
        ) : null}
        {c?.capture_rate != null && c.capture_expires_at ? (
          <span className="t2 narrow">
            {t('app.narrow.capture', {
              rate: percent(number, c.capture_rate),
              time: dateTime(c.capture_expires_at),
            })}
          </span>
        ) : null}
      </td>
      <td>
        {suspended || killed ? (
          <Status s="fire" glyph="×" label={t('app.stopped')} />
        ) : (
          <Status s="ok" glyph="✓" label={t('app.ingesting')} />
        )}
      </td>
      <td className="wrap wide" translate="no">
        {keys}
      </td>
      <td className="wide">{kill}</td>
      <td className="wide">{capture}</td>
      <td className="act">
        <RowMenu
          label={t('app.menu', { app: app.name })}
          items={[
            { label: t('app.captureSet'), onSelect: () => onOpen('capture') },
            ...(c?.capture_rate != null
              ? [{ label: t('app.captureClear'), onSelect: () => onOpen('clear') }]
              : []),
            killed
              ? { label: t('app.killOff'), onSelect: () => onOpen('killOff') }
              : { label: t('app.killOn'), danger: true, onSelect: () => onOpen('killOn') },
          ]}
        />
      </td>
    </tr>
  )
}

/** The app's form window under its project's table: what the change needs, then the Motivo. */
function AppActionWindow({
  app,
  orgId,
  form,
  onClose,
}: {
  app: AppDetail
  orgId: string
  form: AppForm
  onClose: () => void
}) {
  const { t } = useI18n()
  const refresh = [['org', orgId]]
  const kill = useAdminMutation(setKillSwitch, refresh)
  const capture = useAdminMutation(setCapture, refresh)
  const clear = useAdminMutation(clearCapture, refresh)
  const rateId = useId()
  const [expiry, setExpiry] = useState(() => (form === 'capture' ? utcIn(24) : ''))
  const named = (key: MessageId) => t('app.of', { action: t(key), app: app.name })
  const shown = { open: true, onClose }
  if (form === 'killOff')
    return (
      <ActionForm
        {...shown}
        label={named('app.killOff')}
        confirm={t('app.killOffConfirm')}
        onSubmit={(reason) =>
          kill.mutateAsync({ appId: app.id, on: false, reason, expiresAt: null })
        }
      />
    )
  if (form === 'killOn')
    return (
      <ActionForm
        {...shown}
        label={named('app.killOn')}
        confirm={t('app.killOnConfirm')}
        danger
        onSubmit={(reason) =>
          kill.mutateAsync({
            appId: app.id,
            on: true,
            reason,
            expiresAt: expiry.trim() ? parseUtc(expiry) : null,
          })
        }
      >
        <UtcField label={t('app.expiresOptional')} value={expiry} onChange={setExpiry} />
      </ActionForm>
    )
  if (form === 'clear')
    return (
      <ActionForm
        {...shown}
        label={named('app.captureClear')}
        confirm={t('app.captureClearConfirm')}
        onSubmit={(reason) => clear.mutateAsync({ appId: app.id, reason })}
      />
    )
  return (
    <ActionForm
      {...shown}
      label={named('app.captureSet')}
      confirm={t('app.captureSetConfirm')}
      onSubmit={(reason, data) =>
        capture.mutateAsync({
          appId: app.id,
          rate: Number(data.get('rate')) / 100,
          reason,
          expiresAt: parseUtc(expiry) ?? '',
        })
      }
    >
      <div className="field-d">
        <label className="lbl" htmlFor={rateId}>
          {t('app.rate')}
        </label>
        <input
          id={rateId}
          name="rate"
          className="input mono num-field"
          type="number"
          min={0}
          max={100}
          step={0.1}
          defaultValue={10}
          required
        />
      </div>
      <UtcField label={t('app.expires')} value={expiry} onChange={setExpiry} required />
    </ActionForm>
  )
}
